import React, { useEffect, useState } from "react";
import { getRouteDetail } from "../lib/api";
import { statusColor } from "../lib/status";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";

const RouteModal = ({ routeId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getRouteDetail(routeId)
      .then((d) => active && setDetail(d))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [routeId]);

  const maxFactor = detail ? Math.max(0.01, ...detail.factors.map((f) => f.weight)) : 1;
  const timeline = detail
    ? detail.timeline.map((t) => ({ window: t.window, delay: t.delay_minutes, status: t.status }))
    : [];

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="route-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="panel-title" style={{ fontSize: 24 }}>
              <div className="p-ic"><i className="fa-solid fa-route" /></div>
              {detail ? detail.name : "Corridor"}
            </div>
            {detail && (
              <div className="rr-sub" style={{ marginTop: 8, marginLeft: 52 }}>
                {detail.from_place} → {detail.to_place} · {detail.region} · {detail.distance_km} km
              </div>
            )}
          </div>
          <button className="modal-close" onClick={onClose} data-testid="modal-close-btn">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="modal-body">
          {loading && <div className="rr-sub">Loading corridor forecast…</div>}

          {detail && (
            <>
              <div style={{ marginBottom: 14 }}>
                <span className={`pill ${detail.risk_level}`}>{detail.status} · Next 60 min</span>
              </div>

              <div className="detail-grid">
                <div className="detail-cell">
                  <div className="d-val" style={{ color: statusColor(detail.status) }}>+{detail.delay_minutes}m</div>
                  <div className="d-lab">Predicted delay</div>
                </div>
                <div className="detail-cell">
                  <div className="d-val">{detail.eta_minutes}m</div>
                  <div className="d-lab">Total ETA</div>
                </div>
                <div className="detail-cell">
                  <div className="d-val">{Math.round(detail.congestion_index * 100)}%</div>
                  <div className="d-lab">Congestion</div>
                </div>
                <div className="detail-cell">
                  <div className="d-val">{detail.current_speed_kmh}</div>
                  <div className="d-lab">Now km/h</div>
                </div>
                <div className="detail-cell">
                  <div className="d-val">{detail.free_speed_kmh}</div>
                  <div className="d-lab">Free-flow km/h</div>
                </div>
                <div className="detail-cell">
                  <div className="d-val">{detail.free_flow_min}m</div>
                  <div className="d-lab">Clear-road time</div>
                </div>
              </div>

              <div className="section-label"><i className="fa-solid fa-chart-column" /> 60-Minute Delay Forecast</div>
              <div style={{ width: "100%", height: 180 }} data-testid="route-timeline-chart">
                <ResponsiveContainer minWidth={0} minHeight={180}>
                  <BarChart data={timeline} margin={{ top: 6, right: 10, left: -20, bottom: 0 }}>
                    <XAxis dataKey="window" tick={{ fill: "#5b7186", fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#5b7186", fontSize: 12 }} axisLine={false} tickLine={false} unit="m" />
                    <Tooltip formatter={(v) => [`${v} min`, "Delay"]} contentStyle={{ borderRadius: 12, fontWeight: 600 }} />
                    <Bar dataKey="delay" radius={[6, 6, 0, 0]}>
                      {timeline.map((t, i) => (
                        <Cell key={i} fill={statusColor(t.status)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="section-label"><i className="fa-solid fa-sliders" /> Why (contributing factors)</div>
              <div className="factor-list">
                {detail.factors.length === 0 && <div className="rr-sub">Free-flow — no significant pressure detected.</div>}
                {detail.factors.map((f, i) => (
                  <div key={i} className="factor-row">
                    <span className="f-lab">{f.label}</span>
                    <div className="factor-bar">
                      <span style={{ width: `${(f.weight / maxFactor) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              {detail.active_incidents.length > 0 && (
                <>
                  <div className="section-label"><i className="fa-solid fa-triangle-exclamation" /> Active Incidents</div>
                  <div className="feed">
                    {detail.active_incidents.map((inc) => (
                      <div key={inc.id} className={`feed-item ${inc.severity}`}>
                        <div className={`feed-ic ${inc.severity}`}><i className="fa-solid fa-triangle-exclamation" /></div>
                        <div className="feed-body">
                          <div className="feed-title">{inc.type} · {inc.location}</div>
                          <div className="feed-desc">{inc.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteModal;
